import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  ShoppingCart, 
  Users, 
  Target,
  RefreshCw,
  Award,
  BarChart3,
  Activity,
  CalendarRange,
  FileSpreadsheet,
  FileText,
  Loader2
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR').format(value) + ' FCFA';
};

const StatCard = ({ title, value, subValue, icon: Icon, trend, trendValue, color = "green" }) => {
  const colorClasses = {
    green: "bg-green-50 text-green-600",
    blue: "bg-blue-50 text-blue-600", 
    orange: "bg-orange-50 text-orange-600",
    purple: "bg-purple-50 text-purple-600"
  };
  
  return (
    <Card className="border-[#E8DDD4]">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-[#78716C] mb-1">{title}</p>
            <p className="text-2xl font-bold text-[#0F172A]">{value}</p>
            {subValue && (
              <p className="text-xs text-[#78716C] mt-1">{subValue}</p>
            )}
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
        {trend !== undefined && (
          <div className="mt-3 flex items-center gap-1">
            {trend >= 0 ? (
              <TrendingUp className="w-4 h-4 text-green-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500" />
            )}
            <span className={`text-sm font-medium ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {trend >= 0 ? '+' : ''}{trendValue}%
            </span>
            <span className="text-xs text-[#78716C] ml-1">vs sem. préc.</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PerformanceDashboard = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [agentsPerformance, setAgentsPerformance] = useState([]);
  const [dailyEvolution, setDailyEvolution] = useState([]);
  
  // Custom date range state
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const fetchDashboardData = React.useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [weeklyRes, productsRes, agentsRes, evolutionRes] = await Promise.all([
        axios.get(`${API_URL}/stats/dashboard/weekly`, { headers }),
        axios.get(`${API_URL}/stats/dashboard/top-products?limit=5`, { headers }),
        axios.get(`${API_URL}/stats/dashboard/agents-performance`, { headers }),
        axios.get(`${API_URL}/stats/dashboard/daily-evolution?days=7`, { headers })
      ]);
      
      setWeeklyStats(weeklyRes.data);
      setTopProducts(productsRes.data);
      setAgentsPerformance(agentsRes.data);
      setDailyEvolution(evolutionRes.data);
      
      if (showRefresh) toast.success('Données actualisées');
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Export Excel for custom range
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(
        `${API_URL}/stats/export-excel-range?start_date=${startDate}&end_date=${endDate}`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const startFormatted = startDate.replace(/-/g, '');
      const endFormatted = endDate.replace(/-/g, '');
      link.setAttribute('download', `SADA_Rapport_${startFormatted}_${endFormatted}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export Excel téléchargé!');
    } catch (error) {
      toast.error('Erreur d\'export Excel');
    } finally {
      setExporting(false);
    }
  };

  // Export PDF for custom range
  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(
        `${API_URL}/stats/export-pdf-range?start_date=${startDate}&end_date=${endDate}`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const startFormatted = startDate.replace(/-/g, '');
      const endFormatted = endDate.replace(/-/g, '');
      link.setAttribute('download', `SADA_Rapport_${startFormatted}_${endFormatted}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export PDF téléchargé!');
    } catch (error) {
      toast.error('Erreur d\'export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#2E7D32]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-[#0F172A]">Tableau de bord Performance</h2>
          {weeklyStats?.period && (
            <p className="text-sm text-[#78716C] mt-1">{weeklyStats.period.label}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowCustomRange(!showCustomRange)}
            className="border-[#2E7D32] text-[#2E7D32] hover:bg-[#2E7D32]/10"
          >
            <CalendarRange className="w-4 h-4 mr-2" />
            Export personnalisé
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => fetchDashboardData(true)}
            disabled={refreshing}
            className="border-[#2E7D32] text-[#2E7D32] hover:bg-[#2E7D32]/10"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Custom Export Panel */}
      {showCustomRange && (
        <Card className="border-[#2E7D32]/30 bg-gradient-to-br from-[#2E7D32]/5 to-transparent">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-[#0F172A] flex items-center gap-2">
              <CalendarRange className="w-5 h-5 text-[#2E7D32]" />
              Export personnalisé par période
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="perf-start-date" className="text-sm font-medium text-[#0F172A]">
                  Date de début
                </Label>
                <Input
                  id="perf-start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border-[#E7E5E4]"
                  data-testid="perf-start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="perf-end-date" className="text-sm font-medium text-[#0F172A]">
                  Date de fin
                </Label>
                <Input
                  id="perf-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border-[#E7E5E4]"
                  data-testid="perf-end-date-input"
                />
              </div>
            </div>
            
            {/* Quick date presets */}
            <div className="flex flex-wrap gap-2">
              {[
                { label: '7 derniers jours', days: 7 },
                { label: '30 derniers jours', days: 30 },
                { label: '90 derniers jours', days: 90 },
                { label: 'Cette année', days: 'year' },
              ].map((preset) => (
                <Button
                  key={preset.days}
                  variant="outline"
                  size="sm"
                  className="text-xs border-[#2E7D32]/50 text-[#2E7D32] hover:bg-[#2E7D32]/10"
                  onClick={() => {
                    const end = new Date();
                    let start;
                    if (preset.days === 'year') {
                      start = new Date(end.getFullYear(), 0, 1);
                    } else {
                      start = new Date();
                      start.setDate(start.getDate() - preset.days);
                    }
                    setStartDate(start.toISOString().split('T')[0]);
                    setEndDate(end.toISOString().split('T')[0]);
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>

            {/* Export Buttons */}
            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                onClick={handleExportExcel}
                disabled={exporting || !startDate || !endDate}
                className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white"
                data-testid="perf-export-excel-btn"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                )}
                Télécharger Excel
              </Button>
              
              <Button
                onClick={handleExportPdf}
                disabled={exportingPdf || !startDate || !endDate}
                variant="outline"
                className="border-red-500 text-red-600 hover:bg-red-50"
                data-testid="perf-export-pdf-btn"
              >
                {exportingPdf ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                Télécharger PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Chiffre d'affaires"
          value={formatCurrency(weeklyStats?.current_week?.total_revenue || 0)}
          trend={weeklyStats?.variations?.revenue}
          trendValue={weeklyStats?.variations?.revenue}
          icon={DollarSign}
          color="green"
        />
        <StatCard 
          title="Nombre de ventes"
          value={weeklyStats?.current_week?.total_sales || 0}
          trend={weeklyStats?.variations?.sales}
          trendValue={weeklyStats?.variations?.sales}
          icon={ShoppingCart}
          color="blue"
        />
        <StatCard 
          title="Panier moyen"
          value={formatCurrency(weeklyStats?.current_week?.average_basket || 0)}
          icon={Target}
          color="orange"
        />
        <StatCard 
          title="Ratio conversion"
          value={`${weeklyStats?.current_week?.conversion_ratio || 0}%`}
          subValue={`${weeklyStats?.current_week?.total_diagnoses || 0} diagnostics → ${weeklyStats?.current_week?.total_sales || 0} ventes`}
          icon={Activity}
          color="purple"
        />
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Daily Evolution Chart */}
        <Card className="border-[#E7E5E4]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-[#0F172A] flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#2E7D32]" />
              Évolution des ventes (7 jours)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyEvolution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis 
                    dataKey="day" 
                    tick={{ fontSize: 12, fill: '#78716C' }}
                    axisLine={{ stroke: '#E7E5E4' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#78716C' }}
                    axisLine={{ stroke: '#E7E5E4' }}
                    tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}
                  />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'revenue' ? formatCurrency(value) : value,
                      name === 'revenue' ? 'CA' : name === 'sales' ? 'Ventes' : 'Diagnostics'
                    ]}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E7E5E4',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    name="CA"
                    stroke="#2E7D32" 
                    strokeWidth={2}
                    dot={{ fill: '#2E7D32', strokeWidth: 2 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="diagnoses" 
                    name="Diagnostics"
                    stroke="#8B5CF6" 
                    strokeWidth={2}
                    dot={{ fill: '#8B5CF6', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card className="border-[#E7E5E4]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-[#0F172A] flex items-center gap-2">
              <Award className="w-4 h-4 text-[#2E7D32]" />
              Top 5 Produits vendus
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topProducts.length > 0 ? (
              <div className="space-y-3">
                {topProducts.map((product, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      index === 0 ? 'bg-yellow-100 text-yellow-700' :
                      index === 1 ? 'bg-gray-100 text-gray-600' :
                      index === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-[#F5F5F4] text-[#78716C]'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0F172A] truncate">
                        {product.name}
                      </p>
                      <p className="text-xs text-[#78716C]">
                        {product.quantity} unités • {formatCurrency(product.revenue)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-[#78716C]">
                Aucune vente cette semaine
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Agents Performance Table */}
      <Card className="border-[#E7E5E4]">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium text-[#0F172A] flex items-center gap-2">
            <Users className="w-4 h-4 text-[#2E7D32]" />
            Performance par conseiller
          </CardTitle>
        </CardHeader>
        <CardContent>
          {agentsPerformance.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E7E5E4]">
                    <th className="text-left py-3 px-2 text-xs font-medium text-[#78716C]">Rang</th>
                    <th className="text-left py-3 px-2 text-xs font-medium text-[#78716C]">Conseiller</th>
                    <th className="text-right py-3 px-2 text-xs font-medium text-[#78716C]">Diagnostics</th>
                    <th className="text-right py-3 px-2 text-xs font-medium text-[#78716C]">Ventes</th>
                    <th className="text-right py-3 px-2 text-xs font-medium text-[#78716C]">Conversion</th>
                    <th className="text-right py-3 px-2 text-xs font-medium text-[#78716C]">Panier moy.</th>
                    <th className="text-right py-3 px-2 text-xs font-medium text-[#78716C]">CA</th>
                  </tr>
                </thead>
                <tbody>
                  {agentsPerformance.map((agent, index) => (
                    <tr key={agent.agent_id} className="border-b border-[#E7E5E4]/50 hover:bg-[#F5F5F4]/50">
                      <td className="py-3 px-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          index === 0 ? 'bg-yellow-100 text-yellow-700' :
                          index === 1 ? 'bg-gray-100 text-gray-600' :
                          index === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-[#F5F5F4] text-[#78716C]'
                        }`}>
                          {index + 1}
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <span className="font-medium text-[#0F172A]">{agent.agent_name}</span>
                      </td>
                      <td className="py-3 px-2 text-right text-sm text-[#5D4E37]">
                        {agent.total_diagnoses}
                      </td>
                      <td className="py-3 px-2 text-right text-sm text-[#5D4E37]">
                        {agent.total_sales}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <Badge className={`${
                          agent.conversion_ratio >= 50 ? 'bg-green-100 text-green-700' :
                          agent.conversion_ratio >= 25 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {agent.conversion_ratio}%
                        </Badge>
                      </td>
                      <td className="py-3 px-2 text-right text-sm text-[#5D4E37]">
                        {formatCurrency(agent.average_basket)}
                      </td>
                      <td className="py-3 px-2 text-right font-medium text-[#0F172A]">
                        {formatCurrency(agent.total_revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center text-[#78716C]">
              Aucune donnée de performance cette semaine
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceDashboard;
